/* eslint-disable react-hooks/exhaustive-deps */
import {
  useRef,
  useState,
  useEffect,
  useContext,
  ChangeEventHandler,
} from "react";
import { SpinModal } from "common/elements";
import { btnDelay, useScale } from "common/utils";
import { Camera } from "./Camera";
import { useOpenCv } from "./useOpenCv";
import { QrCodeScan } from "./QrCodeScan";
import { ConnectionMessageType } from "./type";
import { PeerConnection, ConnectionContext } from "./PeerConnection";
import { useExecutableBlockListGenerator } from "./useBlockGenerator";

const Main = ({
  connectedRef,
  peerIdRef,
  setPeerId,
  setConnect,
}: {
  connectedRef: React.MutableRefObject<boolean>;
  peerIdRef: React.MutableRefObject<string>;
  setPeerId: (id: string) => void;
  setConnect: (connect: boolean) => void;
}) => {
  const { width, height } = useScale();
  const ref = useRef<HTMLInputElement>();
  const [scan, setScan] = useState(false);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const connection = useContext(ConnectionContext);
  useOpenCv({ setInitialized: () => setLoading(false) });
  const { handleGenerateBlock } = useExecutableBlockListGenerator();

  useEffect(() => {
    if (connectedRef.current) {
      setLoading(false);
    }
  }, [connectedRef.current]);

  const handleStartConnection = (peerId: string) => {
    setPeerId(peerId);
    setScan(false);
    setConnect(true);
    setLoading(true);
    setTimeout(() => {
      if (!connectedRef.current || !connection.socket) {
        setLoading(false);
        setConnect(false);
      }
    }, 5000);
  };

  const handleSendResult = (result: string) => {
    if (connection.socket) {
      connection.socket.emit("analysis", {
        peer_id: peerIdRef.current,
        data: JSON.stringify({
          type: ConnectionMessageType.BLOCK,
          data: result,
        }),
      });
    }
  };

  const handlePickImage: ChangeEventHandler<HTMLInputElement> = async (e) => {
    const file = e.target.files[0];
    if (file) {
      let src: any;
      const image = new Image();
      image.src = URL.createObjectURL(file);
      image.onload = () => {
        // @ts-ignore
        src = cv.imread(image);
      };

      const reader = new FileReader();
      reader.readAsArrayBuffer(file);
      reader.onload = (e) => {
        const arrayBuffer = reader.result;
        handleGenerateBlock(
          arrayBuffer,
          (labels, blocks, isAiRobotTeacher) => {
            if (labels) {
              // @ts-ignore
              const greenRectangleColor = new cv.Scalar(0, 255, 0, 255);
              labels.forEach((label) => {
                // @ts-ignore
                cv.rectangle(
                  src,
                  // @ts-ignore
                  new cv.Point(
                    label.boundingPolygon[0].x - 10,
                    label.boundingPolygon[0].y - 10
                  ),
                  // @ts-ignore
                  new cv.Point(
                    label.boundingPolygon[2].x + 10,
                    label.boundingPolygon[2].y + 10
                  ),
                  greenRectangleColor,
                  10,
                  // @ts-ignore
                  cv.LINE_AA,
                  0
                );
              });
              if (src) {
                // @ts-ignore
                cv.imshow("captured", src);
              }
            }
            if (blocks.length > 0) {
              handleSendResult(JSON.stringify({ blocks, isAiRobotTeacher }));
            }
            console.log("ai robot teacher: ", isAiRobotTeacher);
          },
          (error) => {
            console.log(error);
          },
          () => {
            if (src) {
              // @ts-ignore
              src.delete();
            }
            ref.current.value = "";
          }
        );
      };
    }
  };

  const handleDownloadImage = () =>
    btnDelay(() => {
      const link = document.createElement("a");
      link.download = "blocks.png";
      link.href = (document.getElementById(
        "captured"
      ) as HTMLCanvasElement).toDataURL();
      link.click();
      link.remove();
    });

  return (
    <>
      {loading && <SpinModal />}

      <div className="flex-row-center w-full h-full">
        <div className="flex-col-center w-full h-full gap-5">
          <canvas
            id="captured"
            className={`flex-co-el flex-center origin-top z-10 bg-textcolor/black ${
              width > height ? "h-[60%] aspect-[16/9]" : "w-[60%] aspect-[9/16]"
            }`}
          />

          <div className="flex-row-center flex-wrap w-[70%] gap-5 alpha">
            <button
              className={`btn ${
                connectedRef.current ? "btn-danger" : "btn-primary"
              }`}
              onClick={() =>
                btnDelay(() => {
                  if (connectedRef.current) {
                    setConnect(false);
                  } else {
                    setScan(true);
                  }
                })
              }
            >
              <p className="text-body text-white text-[16px] !mb-[3px]">
                {connectedRef.current ? "切断" : "接続"}
              </p>
            </button>

            <button
              className="btn btn-primary"
              onClick={() => btnDelay(() => setOpen(true))}
            >
              <p className="text-body text-white text-[16px] !mb-[3px]">動画</p>
            </button>

            <div className="flex-col-el flex-center relative pointer-events-auto cursor-pointer">
              <input
                ref={ref}
                type="file"
                onChange={handlePickImage}
                accept="image/*"
                className="flex-row-el absolute appearance-none w-full h-full opacity-0 z-50 !cursor-pointer"
              />
              <button className="btn btn-primary w-full">
                <p className="text-body text-white text-[16px] !mb-[3px]">
                  ファイル
                </p>
              </button>
            </div>

            <button className="btn btn-primary" onClick={handleDownloadImage}>
              <p className="text-body text-white text-[16px] !mb-[3px]">
                ダウンロード
              </p>
            </button>
          </div>
        </div>
      </div>

      <QrCodeScan
        open={scan}
        setOpen={setScan}
        startConnection={handleStartConnection}
      />

      <Camera
        open={open}
        remote={true}
        setOpen={setOpen}
        success={handleSendResult}
      />
    </>
  );
};

export const Shoot = () => {
  const [connect, setConnect] = useState(false);
  const [connected, _setConnected] = useState(false);
  const connectedRef = useRef(connected);
  const setConnected = (connected: boolean) => {
    connectedRef.current = connected;
    _setConnected(connected);
  };
  const [peerId, _setPeerId] = useState<string | null>();
  const peerIdRef = useRef(peerId);
  const setPeerId = (peerId: string) => {
    peerIdRef.current = peerId;
    _setPeerId(peerId);
  };

  const handleSocketConnect = () => {
    setConnected(true);
  };
  const handleSocketDisconnect = () => {
    setConnected(false);
  };

  return (
    <PeerConnection
      connect={connect}
      callee={false}
      peerIdRef={peerIdRef}
      handleSocketConnect={handleSocketConnect}
      handleSocketDisconnect={handleSocketDisconnect}
    >
      <Main
        connectedRef={connectedRef}
        peerIdRef={peerIdRef}
        setPeerId={setPeerId}
        setConnect={setConnect}
      />
    </PeerConnection>
  );
};
